import { useState } from "react";
import axios from "axios";
import { Facebook, Instagram, Linkedin, Mail, ArrowRight, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { ASSETS, NAV_LINKS } from "../lib/constants";
import { useContent } from "../contexts/ContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, linkedin: Linkedin };

export const Footer = () => {
  const { data: footer } = useContent("footer");
  const { data: contact } = useContent("contact");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const contactEmail = contact?.email || "info@semajmorgan.org";
  const contactAddress = contact?.address || "Detroit & Farmington Hills, Michigan";
  const tagline = footer?.tagline || "Love, Care, Give, and Share";
  const description = footer?.description || "Creating access and opportunity for the next generation — powered by mentorship, education, and community.";
  const copyrightName = footer?.copyright || "Semaj J. Morgan Foundation";
  const newsletterHeading = footer?.newsletter_heading || "Stay close to the mission.";
  const newsletterSubhead = footer?.newsletter_subhead || "Updates on programs, events, and the kids you're helping us champion — straight to your inbox.";
  const newsletterEmailLabel = footer?.newsletter_email_label || "Questions? Email us anytime at";
  const newsletterButtonLabel = footer?.newsletter_button_label || "Subscribe";
  const newsletterPlaceholder = footer?.newsletter_placeholder || "Enter your email";
  const exploreHeading = footer?.explore_heading || "Explore";
  const contactHeading = footer?.contact_heading || "Contact";
  const ctaLabel = footer?.cta_label || "Get involved";
  const ctaHref = footer?.cta_href || "#get-involved";
  const bottomLine = footer?.bottom_line || "Built with love — Love, Care, Give, and Share.";
  const footerLogo = (footer?.logo_url && footer.logo_url.trim()) ? footer.logo_url : ASSETS.foundationLogo;
  const socialLinks = (footer?.social_links || []).filter((l) => l?.url);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list — talk soon!");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#1D4ED8] text-white pt-20 pb-10 relative overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[1100px] h-[400px] bg-[#2563EB]/40 blur-[120px] rounded-full"
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Newsletter band */}
        <div className="grid lg:grid-cols-12 gap-8 items-center pb-14 border-b border-white/10">
          <div className="lg:col-span-7">
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              {newsletterHeading}
            </h3>
            <p className="mt-3 text-white/70 max-w-md">
              {newsletterSubhead}
            </p>
            <p className="mt-3 text-sm text-white/60">
              {newsletterEmailLabel}{" "}
              <a
                href={`mailto:${contactEmail}`}
                className="text-[#FFD100] font-semibold hover:underline"
                data-testid="newsletter-contact-email"
              >
                {contactEmail}
              </a>
            </p>
          </div>
          <form
            onSubmit={onSubscribe}
            data-testid="newsletter-form"
            className="lg:col-span-5 flex gap-2 bg-white/5 backdrop-blur rounded-full p-2 border border-white/10"
          >
            <Mail className="w-5 h-5 text-white/60 ml-3 self-center shrink-0" />
            <input
              type="email"
              required
              data-testid="newsletter-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={newsletterPlaceholder}
              className="flex-1 bg-transparent text-sm placeholder:text-white/50 focus:outline-none px-2 py-2 text-white"
            />
            <button
              type="submit"
              data-testid="newsletter-submit-button"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-[#FFD100] hover:bg-[#FFDB33] text-[#0F172A] font-semibold px-5 py-2.5 rounded-full text-sm transition-all hover:-translate-y-0.5 disabled:opacity-70"
            >
              {newsletterButtonLabel} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        {/* Main footer grid */}
        <div className="grid md:grid-cols-12 gap-10 py-14">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <span className="w-14 h-14 rounded-2xl bg-black grid place-items-center ring-1 ring-white/10">
                <img
                  src={footerLogo.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${footerLogo}` : footerLogo}
                  alt="Semaj J. Morgan Foundation"
                  className="w-12 h-12 object-contain"
                />
              </span>
              <div>
                <p className="font-display text-lg font-bold">{copyrightName}</p>
                <p className="text-xs text-white/60 tracking-[0.18em] uppercase">{tagline}</p>
              </div>
            </div>
            <p className="mt-6 text-sm text-white/70 max-w-sm leading-relaxed">{description}</p>

            {socialLinks.length > 0 && (
              <div className="mt-6 flex gap-2">
                {socialLinks.map((l) => {
                  const Icon = SOCIAL_ICONS[l.label?.toLowerCase().split(" ")[0]] || LinkIcon;
                  return (
                    <a
                      key={l.label}
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={l.label}
                      data-testid={`footer-social-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#FFD100] hover:text-[#0F172A] grid place-items-center transition-all border border-white/10"
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.18em] font-bold text-white/50">
              {exploreHeading}
            </p>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-white/75 hover:text-[#FFD100] transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-[0.18em] font-bold text-white/50">
              {contactHeading}
            </p>
            <ul className="mt-5 space-y-3 text-sm text-white/75">
              <li>
                <a
                  href={`mailto:${contactEmail}`}
                  className="hover:text-[#FFD100] transition-colors"
                  data-testid="footer-email-link"
                >
                  {contactEmail}
                </a>
              </li>
              <li>{contactAddress}</li>
              <li>
                <a
                  href={ctaHref}
                  data-testid="footer-get-involved-link"
                  className="inline-flex items-center gap-1.5 text-[#FFD100] font-semibold hover:gap-2.5 transition-all"
                >
                  {ctaLabel} <ArrowRight className="w-4 h-4" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} {copyrightName}. All rights reserved.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <a
              href="/privacy"
              data-testid="footer-privacy-link"
              className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/60 hover:text-[#FFD100] transition-colors"
            >
              Privacy Policy
            </a>
            <span className="text-white/20 hidden md:inline">·</span>
            <a
              href="/dashboard"
              data-testid="footer-admin-login-link"
              className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/60 hover:text-[#FFD100] transition-colors"
            >
              Admin Login
            </a>
            <p className="text-xs text-white/40">
              {bottomLine}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
